import { useMemo } from "react";
import ForecastChart from "@/components/dashboard/charts/ForecastChart";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/utils/format";

export default function ForecastSection({
  historicalRevenue,
  historicalExpenses,
  forecastData,
  taxEstimate,
  isLoading,
  error,
  scenario,
  onScenarioChange,
  forecastMonths,
  rangeStart,
  rangeEnd,
}) {
  const monthsLabel = forecastMonths || (forecastData?.length ?? 0);
  const summary = useMemo(() => {
    const totalRevenue = (forecastData || []).reduce((sum, point) => sum + (point.revenue || 0), 0);
    const totalExpenses = (forecastData || []).reduce((sum, point) => sum + (point.expenses || 0), 0);
    return {
      totalRevenue,
      totalExpenses,
      totalNet: totalRevenue - totalExpenses,
    };
  }, [forecastData]);

  return (
    <div className="space-y-4" data-testid="forecast-section">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between" data-testid="forecast-controls">
        <div className="text-sm font-semibold text-slate-700">Forecast‑Szenario</div>
        <div className="flex items-center gap-2" data-testid="forecast-scenario-toggle">
          {[
            { value: "conservative", label: "Konservativ" },
            { value: "neutral", label: "Neutral" },
            { value: "offensive", label: "Offensiv" },
          ].map((option) => (
            <Button
              key={option.value}
              type="button"
              variant={scenario === option.value ? "default" : "outline"}
              size="sm"
              className="text-xs"
              data-testid={`forecast-scenario-${option.value}`}
              onClick={() => onScenarioChange?.(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3" data-testid="forecast-summary">
        {[
          { label: `Prognose Einnahmen (Netto, ${monthsLabel}M)`, value: summary.totalRevenue },
          { label: `Prognose Ausgaben (Netto, ${monthsLabel}M)`, value: summary.totalExpenses },
          { label: `Prognose Netto (${monthsLabel}M)`, value: summary.totalNet },
        ].map((item) => (
          <Card key={item.label} className="border-slate-200">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500">{item.label}</div>
              <div className="mt-2 text-lg font-semibold text-slate-900">
                {formatCurrency(item.value)}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="text-xs text-slate-500" data-testid="forecast-summary-note">
        Forecast basiert auf gleitendem Durchschnitt und Trend der letzten Monate.
      </div>
      <ForecastChart
        historicalRevenue={historicalRevenue}
        historicalExpenses={historicalExpenses}
        forecastData={forecastData}
        taxEstimate={taxEstimate}
        isLoading={isLoading}
        error={error}
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
      />
    </div>
  );
}